import { Injectable, inject, PLATFORM_ID, Optional } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { isPlatformServer } from '@angular/common';

import { BehaviorSubject, catchError, EMPTY, from, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';

import { Auction } from '@/models/auction-house/auction';
import { GlobalState } from '@/models/auction-house/state';
import { Web3Service } from '@/services/web3.service';
import { StarterBidPipe } from '@/pipes/auction-house/starter-bid';
import { environment } from 'src/environments/environment';
import { initialState } from '@/store/auction-house/data.reducers';

@Injectable({
  providedIn: 'root'
})

export class AuctionDataService {

  subscription!: Subscription;

  private auctionData = new BehaviorSubject<Auction[]>([]);
  public auctionData$ = this.auctionData.asObservable();

  private activeIndex = new BehaviorSubject<number>(0);
  public activeIndex$ = this.activeIndex.asObservable();

  constructor(
    private web3Svc: Web3Service,
    private starterBidPipe: StarterBidPipe,
    private apollo: Apollo
  ) {
    console.log('[AuctionDataService] Constructor called, Apollo available:', !!this.apollo);
  }

  public fetchAuction(currentBlock: number, tokenId?: string): Observable<GlobalState['dataState']['activeAuction']> {
    if (!tokenId) return of(initialState.activeAuction);
    if (environment.collection === 'cryptophunks' || environment.collection === 'cryptopunks-v1') return of(initialState.activeAuction);
    return this.getLatestAuctions(Number(tokenId)).pipe(
      switchMap((auction) => {
        return of({
          ...auction,
          currentBlock,
          ...this.calculateTimeLeft(
            auction,
            currentBlock,
          )
        });
      }),
      // tap((auction) => console.log(auction)),
    );
  }

  getLatestAuctions(tokenId: number): Observable<any> {
    return from(this.web3Svc.getPunkOwner(`${tokenId}`)).pipe(
      // Get the latest cig token transfer that matches the tokenId
      switchMap((owner) => {
        return this.fetchCigTransfers(tokenId, owner).pipe(
          switchMap((transfers) => {
            const auctionStartedBlock = transfers[0]?.blockNumber;
            return this.getAuctionFromTokenId(tokenId, auctionStartedBlock);
          }),
          map((auction) => {
            return {
              ...auction,
              owner,
            };
          })
        )
      }),

    );
  }

  fetchCigTransfers(tokenId: number, owner: string): Observable<any> {

    if (!this.apollo) {
      console.warn('[AuctionDataService] Apollo not available for fetchCigTransfers');
      return EMPTY;
    }

    const sentAmount = this.starterBidPipe.transform(tokenId);
    const auctionStarterAddress = environment.auctionStarterAddress;

    const GET_CIG_TRANSFERS = gql`
      query GetAuctionStarted($owner: String!, $sentAmount: String!, $auctionStarterAddress: String!) {
        transfers(
          orderBy: blockNumber
          orderDirection: desc
          where: { from: $owner, to: $auctionStarterAddress, value: $sentAmount }
        ) {
          from
          to
          blockNumber
          transactionHash
          value
        }
      }
    `;

    return this.apollo.use('cig').query({
      query: GET_CIG_TRANSFERS,
      variables: { owner, sentAmount, auctionStarterAddress },
    }).pipe(
      map((res: any) => res.data.transfers),
      // tap((res) => console.log({res})),
      catchError((error) => {
        console.error('Error fetching listingsCall:', error);
        return EMPTY;
      })
    );
  }

  getAuctionFromTokenId(
    tokenId: number, 
    auctionStartedBlock: string
  ): Observable<Partial<GlobalState['dataState']['activeAuction']>> {
    const defaultRes =  { punkId: `${tokenId}`, startBlock: 0, bid: null, events: []};
    if (!tokenId || !auctionStartedBlock) return of(defaultRes);

    const GET_BIDS = gql`
      query GetActiveBid($tokenId: Int!, $auctionStartedBlock: String!) {
        punk(id: $tokenId) {
          id
          owner {
            id
          }
        }
        bids(
          where: {
            punk_: { id: $tokenId },
            blockNumber_gte: $auctionStartedBlock
          }
        ) {
          id
          value
          transactionHash
          blockNumber
          fromAccount {
            id
          }
        }
        events(
          where: {
            type: "BidEntered", 
            tokenId: $tokenId,
            blockNumber_gte: $auctionStartedBlock
          }
          orderBy: blockNumber
          orderDirection: desc
        ) {
          tokenId
          type
          value
          blockNumber,
          transactionHash,
          fromAccount {
            id
          }
        }
      }
    `;

    if (!this.apollo) {
      console.warn('[AuctionDataService] Apollo not available for getAuctionFromTokenId');
      return of(defaultRes);
    }

    return this.apollo.query({
      query: GET_BIDS,
      variables: { tokenId, auctionStartedBlock }
    }).pipe(
      map((res: any) => {
        return {
          punkId: `${tokenId}`,
          bid: res.data?.bids[0],
          events: res.data?.events,
          startBlock: Number(auctionStartedBlock)
        } as GlobalState['dataState']['activeAuction']
      }),
      catchError((err) => {
        console.log(err);
        return of(defaultRes);
      })
    );
  }

  calculateTimeLeft(
    auction: any,
    currentBlock: number,
  ): {
    endTime: number, 
    secondsLeft: number, 
    blocksRemaining: number,
    settled: boolean,
  } {
    const startBlock = auction.startBlock;
    const maxBlocks = environment.auctionMaxBlocks; // Total blocks for the auction under normal conditions
    const criticalWindow = environment.extendedBlocks; // Bids within this range from the end extend the auction

    let endBlock = startBlock + maxBlocks;
  
    auction.events.forEach((evnt: any) => {
      const bidBlock = Number(evnt.blockNumber);
      // If a bid is within the critical window of the current end block
      if (bidBlock >= endBlock - criticalWindow) {
        // Extend the auction to ensure there are 25 blocks after the bid
        // This effectively sets the endBlock to 25 blocks after the bidBlock
        endBlock = bidBlock + criticalWindow;
      }
    });
  
    // Calculate remaining blocks
    const blocksRemaining = endBlock - currentBlock;
  
    // Calculate seconds left (assuming you have a constant for seconds per block)
    let secondsLeft = blocksRemaining * environment.blockTimeInSec;
  
    return {
      endTime: (new Date().getTime() + (secondsLeft * 1000)),
      blocksRemaining,
      secondsLeft,
      settled: (blocksRemaining + environment.unsettledBlocks) < 0,
    };
  }
}
